import { useState, useEffect } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Box,
  Button,
  Card,
  CardContent,
  Fab,
  Grow,
  Stack,
  Typography,
} from '@mui/material';
import { ClickAwayListener } from '@mui/material';
import { createPortal } from 'react-dom';
import { ExternalUrls } from '@/constants';
import { useScreenSizes } from '@/hooks/use-screen-sizes';

const qrCodeImageUrl =
  'https://assets.galena.com/edupass-api/public/whatsapp_qr_code.svg';

const mailToUrl = 'mailto:atendimento@galena.com';
const helpCentralUrl = 'https://atendimento.galena.com/';

const texts = {
  title: 'Precisa de ajuda?',
  descriptionDesktop:
    'Aponte a câmera do seu celular para falar com a gente pelo WhasApp.',
  descriptionMobile: 'Clique no botão para falar com a Galena via WhatsApp.',
  openButton: 'Abrir menu de ajuda',
  closeButton: 'Fechar menu de ajuda',
  whatsappButton: 'Abrir WhatsApp',
  emailButton: 'Email: atendimento@galena.com',
  helpCentralButton: 'Central de Dúvidas',
} as const;

const usePreloadImage = (src: string) => {
  useEffect(() => {
    const img = new Image();
    img.src = src;
  }, [src]);
};

const useEscapeToClose = (onClose: () => void) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
};

interface HelpContentProps {
  onClose: () => void;
}

const HelpContent = ({ onClose }: HelpContentProps) => {
  const { mobileSize } = useScreenSizes();
  const showQrCode = !mobileSize;

  const description = mobileSize
    ? texts.descriptionMobile
    : texts.descriptionDesktop;

  return (
    <Grow
      in
      style={{ transformOrigin: 'bottom right' }}
      timeout={300}
      role="presentation"
    >
      <Box
        sx={(theme) => ({
          bottom: 60,
          position: 'absolute',
          right: 0,
          width: `calc(100% - (2 * ${theme.spacing(1.5)}))`,

          [theme.breakpoints.up('sm')]: {
            bottom: 70,
            width: 350,
          },
        })}
      >
        <ClickAwayListener
          onClickAway={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
        >
          <Card elevation={2} raised sx={{ padding: 6 }}>
            <CardContent
              sx={{
                padding: 0,
                textAlign: 'center',
              }}
            >
              <Stack spacing={4} alignItems="center">
                <Typography component="p" variant="h3">
                  {texts.title}
                </Typography>
                <Stack spacing={2} alignItems="center">
                  <Button
                    href={helpCentralUrl}
                    target="_blank"
                    variant="outlined"
                  >
                    {texts.helpCentralButton}
                  </Button>
                  <Button href={mailToUrl} variant="outlined">
                    {texts.emailButton}
                  </Button>
                  <Button
                    color="success"
                    href={ExternalUrls.WhatsappUrl}
                    startIcon={<WhatsAppIcon />}
                    target="_blank"
                    variant="contained"
                  >
                    {texts.whatsappButton}
                  </Button>
                </Stack>
                {showQrCode && (
                  <Box
                    component="img"
                    src={qrCodeImageUrl}
                    alt="QR code para contato via WhatsApp"
                    sx={{ width: 160 }}
                  />
                )}
              </Stack>
              <Typography sx={{ mt: { xs: 4, sm: 0 } }}>
                {description}
              </Typography>
            </CardContent>
          </Card>
        </ClickAwayListener>
      </Box>
    </Grow>
  );
};

interface HelpFabProps {
  isOpen: boolean;
  onClick: () => void;
}

const HelpFab = ({ isOpen, onClick }: HelpFabProps) => {
  const ariaLabel = isOpen ? texts.closeButton : texts.openButton;
  const icon = isOpen ? (
    <CloseRoundedIcon sx={{ fontSize: 20 }} />
  ) : (
    <HelpOutlineRoundedIcon sx={{ fontSize: 20 }} />
  );

  return (
    <Fab
      aria-label={ariaLabel}
      variant="extended"
      onClick={onClick}
      sx={{
        bottom: 60,
        height: 50,
        letterSpacing: 1,
        position: 'absolute',
        right: 0,
        textTransform: 'none',
        transition:
          'transform 0.15s ease, width 0.1s ease, background-color 0.5s ease',
        width: 'fit-content',
        '&:hover': { transform: 'scale(1.1)' },
        '&:active': { transform: 'scale(1)' },
        backgroundColor: 'primary.dark',
        color: 'white',
      }}
    >
      {icon}
    </Fab>
  );
};

const HelpMenuContent = () => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => setIsOpen((prev) => !prev);
  const handleClose = () => setIsOpen(false);

  usePreloadImage(qrCodeImageUrl);
  useEscapeToClose(handleClose);

  return (
    <Box
      className="HelpMenu media-print-remove"
      sx={(theme) => ({
        bottom: theme.spacing(1.5),
        position: 'fixed',
        right: theme.spacing(1.5),
        width: '100%',
        zIndex: theme.zIndex.fab,

        [theme.breakpoints.up('sm')]: {
          bottom: theme.spacing(3),
          right: theme.spacing(3),
        },
      })}
    >
      {isOpen && <HelpContent onClose={handleClose} />}
      <HelpFab isOpen={isOpen} onClick={handleToggle} />
    </Box>
  );
};

export const HelpMenu = () => {
  return createPortal(<HelpMenuContent />, document.body);
};
